import React from 'react';
import { Checkbox, CheckboxWithLabel } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import Link from 'next/link';

import * as styles from './styles';

const doNothing = () => undefined;

type CheckboxData = {
  checked?: boolean;
  disabled?: boolean;
  href?: string;
  labelText: string;
  secondaryLabelText?: string;
  value: string;
};

export interface CheckboxGroupProps {
  accessibleLabel: string;
  accessibleLabelElementId?: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  checkboxData: CheckboxData[];
  hideLabel?: boolean;
  name: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CheckboxGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  onChange,
  checkboxData,
  hideLabel,
}: CheckboxGroupProps) => {
  return (
    <div aria-labelledby={accessibleLabelElementId} role="group">
      <span css={hideLabel ? visuallyHidden : undefined} id={accessibleLabelElementId}>
        <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
      </span>
      {checkboxData.map(
        ({ checked, disabled, href, labelText, secondaryLabelText, value: checkboxValue }) => {
          const showLink = Boolean(href && !disabled);
          const linkIsPrimaryInteractive = showLink && !onChange;
          const checkboxInputId = `${name}-${checkboxValue}`;
          const accessibleLinkName = `${labelText}${
            secondaryLabelText ? ` ${secondaryLabelText}` : ''
          }`;

          const linkedCheckbox = (
            <>
              <Link
                aria-current={linkIsPrimaryInteractive && checked ? 'true' : undefined}
                aria-hidden={linkIsPrimaryInteractive ? undefined : 'true'}
                css={styles.linkUnderlay}
                data-testid={`${checkboxValue}-link`}
                href={href!}
                prefetch={false}
                rel="nofollow"
                scroll={false}
                shallow
                tabIndex={linkIsPrimaryInteractive ? undefined : -1}
              >
                <span css={visuallyHidden}>{accessibleLinkName}</span>
              </Link>
              <span css={styles.checkboxLabelForeground}>
                <Checkbox
                  aria-hidden={linkIsPrimaryInteractive ? true : undefined}
                  checked={checked}
                  disabled={disabled}
                  id={checkboxInputId}
                  name={name}
                  onChange={onChange ?? doNothing}
                  tabIndex={linkIsPrimaryInteractive ? -1 : undefined}
                  value={checkboxValue}
                />
                <span
                  css={styles.checkboxLabelTextSpan}
                  dangerouslySetInnerHTML={{ __html: labelText }}
                />
              </span>
            </>
          );

          const checkboxRow = linkIsPrimaryInteractive ? (
            <span
              css={[
                styles.checkboxLabelBase,
                disabled && styles.checkboxLabelDisabledState,
                styles.checkboxWrapper,
              ]}
            >
              {linkedCheckbox}
            </span>
          ) : showLink ? (
            <label
              css={[
                styles.checkboxLabelBase,
                disabled && styles.checkboxLabelDisabledState,
                styles.checkboxWrapper,
              ]}
              htmlFor={checkboxInputId}
            >
              {linkedCheckbox}
            </label>
          ) : (
            <CheckboxWithLabel
              checked={checked}
              css={styles.checkboxWrapper}
              disabled={disabled}
              label={labelText}
              name={name}
              onChange={onChange ?? doNothing}
              value={checkboxValue}
            />
          );

          return (
            <div
              css={styles.checkboxWrapperContainer}
              data-testid={checkboxValue}
              key={checkboxValue}
            >
              {checkboxRow}
              {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
            </div>
          );
        },
      )}
    </div>
  );
};
